import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Calcula o tamanho ideal da fonte baseado no comprimento do texto
 * Tamanho base: 14px (text-sm), com redução automática para títulos longos
 * @param text - Texto a ser analisado
 * @returns Classe CSS do tamanho da fonte
 */
export function calculateFontSize(text: string): string {
  if (!text) return 'text-sm'; // 14px como base

  const length = text.length;

  if (length <= 15) {
    return 'text-base'; // Títulos muito curtos - fonte um pouco maior (16px)
  } else if (length <= 25) {
    return 'text-sm'; // Títulos curtos - fonte base (14px)
  } else if (length <= 35) {
    return 'text-xs'; // Títulos médios - fonte menor (12px)
  } else if (length <= 45) {
    return 'text-xs'; // Títulos longos - mantém fonte pequena (12px)
  } else {
    return 'text-xs'; // Títulos muito longos - fonte mínima (12px)
  }
}

/**
 * Converte um texto para caixa alta (uppercase) seguindo as regras de padronização visual
 * @param text - Texto a ser convertido
 * @returns Texto em caixa alta
 */
export function toUppercase(text: string): string {
  if (!text) return '';
  return text.toUpperCase();
}

/**
 * Remove classes de tamanho de fonte (text-*) de uma string de classes CSS
 * @param className - String de classes CSS
 * @returns Classes CSS sem as classes de tamanho de fonte
 */
export function removeFontSizeClasses(className: string): string {
  if (!className) return '';

  // Regex para encontrar classes text-* (text-xs, text-sm, text-base, text-lg, text-xl, text-2xl, etc.)
  const fontSizeRegex = /\btext-\w+/g;

  return className.replace(fontSizeRegex, '').trim();
}

/**
 * Gera estilos CSS inline para redução automática de fonte baseada na largura
 * @param text - Texto a ser analisado
 * @returns Objeto com estilos CSS
 */
export function generateResponsiveFontStyles(text: string): React.CSSProperties {
  if (!text) {
    return { fontSize: '14px' };
  }

  const length = text.length;
  // Sem valor inicial: a cadeia if/else abaixo tem `else` terminal, logo cada
  // caminho atribui. Inicializar aqui tornava um dos ramos redundante.
  let baseFontSize: number;

  // Ajusta o tamanho base baseado no comprimento
  if (length <= 15) {
    baseFontSize = 16; // Títulos muito curtos - um pouco maior
  } else if (length <= 25) {
    baseFontSize = 14; // Títulos curtos - tamanho base
  } else if (length <= 35) {
    baseFontSize = 12; // Títulos médios - menor
  } else if (length <= 45) {
    baseFontSize = 11; // Títulos longos - ainda menor
  } else {
    baseFontSize = 10; // Títulos muito longos - mínimo
  }

  return {
    fontSize: `${baseFontSize}px`,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    minWidth: 0,
    // Redução automática se não couber
    fontSizeAdjust: '0.5', // Ajusta proporção se necessário
  };
}

/**
 * Aplica padronização completa ao texto: caixa alta + ajuste de fonte
 * @param text - Texto a ser processado
 * @returns Objeto com texto padronizado e classe de fonte
 */
export function standardizeTitle(text: string): { text: string; fontSize: string } {
  const upperText = toUppercase(text);
  const fontSize = calculateFontSize(upperText);

  return {
    text: upperText,
    fontSize
  };
}
